import React from 'react';
import adminApi from '../../../services/adminApi';
import { formatDateTime, formatPct } from '../Campaigns/campaignUtils';
import styles from '../Campaigns/AdminCampaigns.module.css';

// Admin → Emails: the notification emails the worker sends (daily digest,
// onboarding, coin / collection nudges …). Campaigns have their own report.

const STATUS_LABELS = {
    queued: 'Queued',
    sent: 'Sent',
    delivered: 'Delivered',
    bounced: 'Bounced',
    complained: 'Spam complaint',
    failed: 'Failed to queue',
};

function useDebounced(value, ms) {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

function Tile({ label, value, pct, na }) {
    return (
        <div className={styles.metric}>
            <div className={styles.metricLabel}>{label}</div>
            {na ? <div className={styles.metricNA}>Not available</div> : (
                <>
                    <div className={styles.metricValue}>{(value ?? 0).toLocaleString()}</div>
                    {pct !== null && pct !== undefined && <div className={styles.metricSub}>{formatPct(pct)}</div>}
                </>
            )}
        </div>
    );
}

const cell = (value, pct, feedback) => {
    if (!feedback) return <span className={styles.muted}>—</span>;
    return <>{(value ?? 0).toLocaleString()}{pct !== null && pct !== undefined && <span className={styles.muted}> · {formatPct(pct)}</span>}</>;
};

export default function AdminEmails() {
    const [range, setRange] = React.useState({ from: '', to: '' });
    const [summary, setSummary] = React.useState(null);
    const [summaryError, setSummaryError] = React.useState('');
    const [filters, setFilters] = React.useState({ type: '', status: '', page: 1 });
    const [search, setSearch] = React.useState('');
    const q = useDebounced(search.trim(), 350);
    const [list, setList] = React.useState(null);
    const [listError, setListError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const rangeParams = React.useMemo(() => ({
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
    }), [range]);

    React.useEffect(() => {
        let cancelled = false;
        setSummaryError('');
        adminApi.getEmailNotificationSummary(rangeParams)
            .then((data) => {
                if (cancelled) return;
                setSummary(data);
                // Show the server's default window in the date inputs.
                setRange((r) => (r.from || r.to ? r : { from: data.range.from, to: data.range.to }));
            })
            .catch((err) => { if (!cancelled) setSummaryError(err.message || 'Could not load the report.'); });
        return () => { cancelled = true; };
    }, [rangeParams]);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setListError('');
        adminApi.listEmailNotifications({
            ...rangeParams,
            ...(filters.type ? { type: filters.type } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(q ? { q } : {}),
            page: filters.page,
        })
            .then((data) => { if (!cancelled) setList(data); })
            .catch((err) => { if (!cancelled) setListError(err.message || 'Could not load sends.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [rangeParams, filters, q]);

    // Reset to page 1 when the search changes.
    React.useEffect(() => { setFilters((f) => (f.page === 1 ? f : { ...f, page: 1 })); }, [q]);

    const feedback = Boolean(summary?.providerFeedback);
    const o = summary?.overall;
    const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value, page: 1 }));
    const typeOptions = summary?.typeOptions || [];

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>Emails</h1>
                    <p className={styles.subtitle}>
                        Notification emails Aluvo sends automatically: daily digest, onboarding, coin and collection nudges.
                        Campaigns are reported on the Campaigns page. Password resets and verification emails are not tracked.
                    </p>
                </div>
            </div>

            <div className={styles.filters}>
                <label className={styles.field} style={{ marginBottom: 0 }}>
                    <span className={styles.label}>From</span>
                    <input type="date" className={styles.input} value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} aria-label="From" />
                </label>
                <label className={styles.field} style={{ marginBottom: 0 }}>
                    <span className={styles.label}>To</span>
                    <input type="date" className={styles.input} value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} aria-label="To" />
                </label>
            </div>

            {summaryError && <div className={styles.errorBanner}>{summaryError}</div>}

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Overview</h2>
                <p className={styles.cardHint}>
                    {feedback
                        ? 'Delivered, opened and clicked come from Mailgun. Opened / clicked % are of delivered emails. Opens can be overstated by mail apps that preload images.'
                        : 'Delivery, open and click data need Mailgun webhooks (CAMPAIGN_PROVIDER_FEEDBACK=true). Only queued counts are shown.'}
                </p>
                {o && (
                    <div className={styles.metricsGrid} data-testid="email-overview">
                        <Tile label="Queued" value={o.total} />
                        <Tile label="Delivered" value={o.delivered} pct={o.deliveredPct} na={!feedback} />
                        <Tile label="Opened" value={o.opened} pct={o.openedPct} na={!feedback} />
                        <Tile label="Clicked" value={o.clicked} pct={o.clickedPct} na={!feedback} />
                        <Tile label="Bounced" value={o.bounced} pct={o.bouncedPct} na={!feedback} />
                        <Tile label="Failed to queue" value={o.failed} />
                    </div>
                )}
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>By email</h2>
                <p className={styles.cardHint}>Click a row to see who received that email.</p>
                {summary && summary.types.length === 0 && <p className={styles.muted}>No notification emails in this period.</p>}
                {summary && summary.types.length > 0 && (
                    <div className={styles.tableWrap}>
                        <table className={styles.table} data-testid="email-types">
                            <thead>
                                <tr><th>Email</th><th>Queued</th><th>Delivered</th><th>Opened</th><th>Clicked</th><th>Bounced</th><th>Failed</th></tr>
                            </thead>
                            <tbody>
                                {summary.types.map((t) => (
                                    <tr
                                        key={t.type}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setFilters((f) => ({ ...f, type: t.type, page: 1 }))}
                                    >
                                        <td><strong>{t.label}</strong></td>
                                        <td>{t.total.toLocaleString()}</td>
                                        <td>{cell(t.delivered, t.deliveredPct, feedback)}</td>
                                        <td>{cell(t.opened, t.openedPct, feedback)}</td>
                                        <td>{cell(t.clicked, t.clickedPct, feedback)}</td>
                                        <td>{cell(t.bounced, t.bouncedPct, feedback)}</td>
                                        <td>{t.failed.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Sent emails</h2>
                <div className={styles.filters}>
                    <input
                        className={`${styles.input} ${styles.searchInput}`}
                        placeholder="Search by username or email"
                        aria-label="Search by username or email"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select className={styles.select} value={filters.type} onChange={setFilter('type')} aria-label="Email type">
                        <option value="">All emails</option>
                        {typeOptions.map((t) => <option key={t.type} value={t.type}>{t.label}</option>)}
                    </select>
                    <select className={styles.select} value={filters.status} onChange={setFilter('status')} aria-label="Status">
                        <option value="">Any status</option>
                        {Object.entries(STATUS_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                </div>
                {listError && <div className={styles.errorBanner}>{listError}</div>}
                {list && list.sends.length === 0 && !loading && <p className={styles.muted}>No emails match these filters.</p>}
                {list && list.sends.length > 0 && (
                    <div className={styles.tableWrap}>
                        <table className={styles.table} data-testid="email-sends">
                            <thead>
                                <tr><th>Sent</th><th>User</th><th>Email</th><th>Subject</th><th>Status</th><th>Opened</th><th>Clicked</th></tr>
                            </thead>
                            <tbody>
                                {list.sends.map((s) => (
                                    <tr key={s.id}>
                                        <td className={styles.nowrap}>{formatDateTime(s.createdAt)}</td>
                                        <td>
                                            {s.accountDeleted ? <span className={styles.muted}>Deleted account</span> : <>@{s.username}</>}
                                            <div className={styles.muted}>{s.email || ''}</div>
                                        </td>
                                        <td>{s.typeLabel}</td>
                                        <td style={{ minWidth: 220 }}>{s.subject}</td>
                                        <td className={styles.nowrap}>{STATUS_LABELS[s.status] || s.status}</td>
                                        <td className={styles.nowrap}>{s.openedAt ? formatDateTime(s.openedAt) : <span className={styles.muted}>—</span>}</td>
                                        <td>
                                            {s.clickedAt ? (
                                                <>
                                                    <span className={styles.nowrap}>{formatDateTime(s.clickedAt)}</span>
                                                    {s.clickedUrl && (
                                                        <div
                                                            className={`${styles.muted} ${styles.urlText}`}
                                                            style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                            title={s.clickedUrl}
                                                        >
                                                            {s.clickedUrl}
                                                        </div>
                                                    )}
                                                </>
                                            ) : <span className={styles.muted}>—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {list && list.pagination.totalPages > 1 && (
                    <div className={styles.pagination}>
                        <span className={styles.muted}>
                            Page {list.pagination.page} of {list.pagination.totalPages} · {list.pagination.total.toLocaleString()} emails
                        </span>
                        <button type="button" className={styles.secondaryBtn} disabled={filters.page <= 1 || loading} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>Previous</button>
                        <button type="button" className={styles.secondaryBtn} disabled={filters.page >= list.pagination.totalPages || loading} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next</button>
                    </div>
                )}
            </div>
        </div>
    );
}
